import { useMemo, useState } from 'react'
import { toHHMMSS } from '@/utils/time'
import Modal from '@/components/layout/Modal'

interface SoberCountdownProps {
  secondsSinceLastDrink: number
  hoursUntilSober: number
  isSober: boolean
}

type SeverityLevel = 'normal' | 'caution' | 'danger'

export default function SoberCountdown({
  secondsSinceLastDrink,
  hoursUntilSober,
  isSober,
}: SoberCountdownProps) {
  const [showModal, setShowModal] = useState(false)

  const timeSinceLast = useMemo(() => {
    const formatted = toHHMMSS(secondsSinceLastDrink)
    if (formatted.includes(',')) {
      const [prefix, time] = formatted.split(', ')
      return { prefix: prefix + ', ', value: time }
    }
    return { prefix: '', value: formatted }
  }, [secondsSinceLastDrink])

  const timeUntilSober = useMemo(() => {
    return toHHMMSS(hoursUntilSober * 3600)
  }, [hoursUntilSober])

  const severity: SeverityLevel = useMemo(() => {
    if (hoursUntilSober >= 6) return 'danger'
    if (hoursUntilSober >= 4) return 'caution'
    return 'normal'
  }, [hoursUntilSober])

  const soberTimeColor = useMemo(() => {
    if (severity === 'danger') return 'text-[var(--bac-danger)]'
    if (severity === 'caution') return 'text-[var(--accent-orange)]'
    return 'text-[var(--text-primary)]'
  }, [severity])

  return (
    <>
      {/* Time Since Last Drink */}
      <div className="card flex flex-col min-w-[120px] flex-shrink-0 snap-start md:min-w-0 md:flex-shrink whitespace-nowrap" role="status" aria-label={`Time since last drink: ${timeSinceLast.prefix}${timeSinceLast.value}`}>
        <p className="text-sm text-[var(--text-secondary)] text-center">Since Last Drink</p>
        <div className="font-mono flex-1 flex items-center justify-center">
          <div className="text-center">
            {timeSinceLast.prefix && (
              <div className="text-lg text-[var(--text-secondary)]">{timeSinceLast.prefix}</div>
            )}
            <span className="text-4xl font-bold text-[var(--text-primary)]">
              {timeSinceLast.value}
            </span>
          </div>
        </div>
      </div>

      {/* Time Until Sober */}
      <div
        className={`card flex flex-col min-w-[120px] flex-shrink-0 snap-start md:min-w-0 md:flex-shrink whitespace-nowrap ${!isSober ? 'cursor-pointer hover:bg-[var(--bg-card)] transition-colors' : ''}`}
        onClick={() => !isSober && setShowModal(true)}
        role="status"
        aria-label={isSober ? 'Status: Sober' : `Time until sober: ${timeUntilSober}`}
      >
        <p className="text-sm text-[var(--text-secondary)] text-center">
          {isSober ? 'Status' : 'Until Sober'}
        </p>
        <div className="flex-1 flex items-center justify-center">
          {isSober ? (
            <div className="flex items-center gap-2">
              <svg
                className="w-10 h-10 text-[var(--bac-safe)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-4xl font-bold text-[var(--bac-safe)]">Sober</span>
            </div>
          ) : (
            <span className={`text-4xl font-bold font-mono ${soberTimeColor}`}>
              {timeUntilSober}
            </span>
          )}
        </div>
        {!isSober && (
          <p className="text-xs text-[var(--text-secondary)] text-center opacity-60">
            Tap for info
          </p>
        )}
      </div>

      {/* Sober Time Explanation Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Time Until Sober"
        size="sm"
      >
        <div className="space-y-4">
          {severity === 'danger' && (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bac-danger)]/20 border border-[var(--bac-danger)]/40">
                <svg className="w-8 h-8 text-[var(--bac-danger)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-bold text-[var(--bac-danger)]">6+ hours until sober</p>
                  <p className="text-sm text-[var(--text-secondary)]">This is a critical level</p>
                </div>
              </div>
              <div className="space-y-3 text-[var(--text-primary)]">
                <p className="font-semibold">You should stop drinking now.</p>
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li className="flex gap-2">
                    <span className="text-[var(--bac-danger)]">•</span>
                    Your body needs many hours to process the alcohol already in your system
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--bac-danger)]">•</span>
                    Additional drinks will push your sobriety even further out
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--bac-danger)]">•</span>
                    You won't be safe to drive until tomorrow
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--bac-danger)]">•</span>
                    Risk of alcohol poisoning increases significantly at these levels
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--bac-danger)]">•</span>
                    Your judgment and coordination are severely impaired
                  </li>
                </ul>
                <p className="text-sm font-medium pt-2 border-t border-[var(--border)]">
                  Consider switching to water and getting something to eat.
                </p>
              </div>
            </>
          )}

          {severity === 'caution' && (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--accent-orange)]/20 border border-[var(--accent-orange)]/40">
                <svg className="w-8 h-8 text-[var(--accent-orange)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-bold text-[var(--accent-orange)]">4-6 hours until sober</p>
                  <p className="text-sm text-[var(--text-secondary)]">Time to slow down</p>
                </div>
              </div>
              <div className="space-y-3 text-[var(--text-primary)]">
                <p className="font-semibold">Consider stopping or slowing down significantly.</p>
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li className="flex gap-2">
                    <span className="text-[var(--accent-orange)]">•</span>
                    You've already had a significant amount to drink
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--accent-orange)]">•</span>
                    Your liver can only process about one standard drink per hour
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--accent-orange)]">•</span>
                    Each additional drink adds roughly another hour until sober
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--accent-orange)]">•</span>
                    Driving is not safe for several hours
                  </li>
                </ul>
                <p className="text-sm font-medium pt-2 border-t border-[var(--border)]">
                  Try alternating with water or non-alcoholic drinks.
                </p>
              </div>
            </>
          )}

          {severity === 'normal' && (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-card)]">
                <svg className="w-8 h-8 text-[var(--text-primary)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-bold text-[var(--text-primary)]">Under 4 hours until sober</p>
                  <p className="text-sm text-[var(--text-secondary)]">Moderate level</p>
                </div>
              </div>
              <div className="space-y-3 text-[var(--text-primary)]">
                <p>Your current alcohol level is moderate.</p>
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li className="flex gap-2">
                    <span className="text-[var(--accent-teal)]">•</span>
                    Your body is processing the alcohol at a normal rate
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--accent-teal)]">•</span>
                    You may still be impaired—avoid driving
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[var(--accent-teal)]">•</span>
                    Staying hydrated helps you feel better
                  </li>
                </ul>
              </div>
            </>
          )}

          <div className="text-xs text-[var(--text-secondary)] pt-2 border-t border-[var(--border)]">
            <p>Time until sober is estimated based on average alcohol metabolism of 0.015% BAC per hour. Individual rates vary.</p>
          </div>
        </div>
      </Modal>
    </>
  )
}
